import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FormControl, Select, MenuItem, Button, InputLabel, Tooltip as MuiTooltip, ToggleButton, ToggleButtonGroup, Chip, ListSubheader, Divider } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { geoAlbersUsa } from 'd3-geo';
import statesTopo from 'us-atlas/states-10m.json';
import ProductSelector from './ProductSelector';
import MotivationPostsDisplay from './MotivationPostsDisplay';
import { apiPost } from '../services/apiClient';

const TIME_RANGE_OPTIONS = [
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 24, label: '24 Months' },
    { value: 36, label: '36 Months' },
];

// Diverging scale around the chain-wide average of the selected metric.
// Blue = above average, red = below, neutral gray midpoint.
// Both arms validated (lightness-monotone, light end ≥2:1 on surface).
const DIVERGING = ['#c73230', '#e34948', '#efa09f', '#f0efec', '#86b6ef', '#3987e5', '#1c5cab'];
const COMPETITOR_COLOR = '#4a3aa7'; // categorical violet — never used by the diverging scale

const INK = '#0b0b0b';
const INK_SECONDARY = '#52514e';
const INK_MUTED = '#898781';
const HAIRLINE = '#e1e0d9';
const SURFACE = '#fcfcfb';
const NO_DATA_BG = '#f7f6f3';

// d3's canonical US map dimensions; the filter projection below must use the
// same defaults as the one react-simple-maps builds for these dimensions.
const MAP_WIDTH = 975;
const MAP_HEIGHT = 610;

// LLM NPS assessments count as meaningful topic coverage above this many posts;
// below it the NPS view falls back to the star-based proxy.
const MIN_LLM_NPS_COVERAGE = 50;

// Momentum compares the last 3 months vs the 3 before; a window needs this
// many data points per location to count.
const MOMENTUM_MIN_N = 10;

const STATE_NAME_TO_CODE = {
    Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
    Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
    Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
    Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
    Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
    Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
    Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
    Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};
const CODE_TO_STATE_NAME = Object.fromEntries(Object.entries(STATE_NAME_TO_CODE).map(([n, c]) => [c, n]));

const divergingColor = (value, midpoint, halfRange) => {
    if (value == null || midpoint == null) return NO_DATA_BG;
    const t = Math.max(-1, Math.min(1, (value - midpoint) / halfRange));
    const bin = Math.min(6, Math.floor(((t + 1) / 2) * 7));
    return DIVERGING[bin];
};

const npsOf = (promoters, detractors, rated) =>
    rated > 0 ? ((promoters - detractors) / rated) * 100 : null;

// Review volume regardless of payload flavor (star/NPS payloads carry
// post_count, dimension payloads carry scored)
const volumeOf = (loc) => loc.post_count ?? loc.scored ?? 0;

// "17801 International Blvd NS-26M, Seattle, WA 98158" -> "Seattle, WA"
const cityStateFromAddress = (address) => {
    if (!address) return null;
    const parts = address.split(',').map(p => p.trim());
    if (parts.length < 3) return null;
    const city = parts[parts.length - 2];
    const state = (parts[parts.length - 1].match(/^[A-Z]{2}/) || [])[0];
    if (!city || !state) return null;
    return `${city}, ${state}`;
};

// "4401 CIG Tampa FL" -> "Tampa FL (#4401)"
const nameFromStoreCode = (key) => {
    const tokens = key.split(' ');
    if (tokens.length >= 3 && /^\d{3,4}$/.test(tokens[0]) && /^[A-Z]{2,4}$/.test(tokens[1])) {
        return `${tokens.slice(2).join(' ')} (#${tokens[0]})`;
    }
    return null;
};

const displayName = (loc) => {
    const parsed = cityStateFromAddress(loc.address) || nameFromStoreCode(loc.key);
    if (parsed) return parsed;
    // Name is usually just the brand; add the street so rows stay distinguishable
    const street = loc.address ? loc.address.split(',')[0].trim() : null;
    if (loc.name && street) return `${loc.name} · ${street}`;
    return loc.name || loc.key;
};

// US state code for a location, from its address tail or named store code
const stateOf = (loc) => {
    if (loc.address) {
        const m = loc.address.trim().match(/,\s*([A-Z]{2})(?:\s+[\d-]+)?$/);
        if (m && CODE_TO_STATE_NAME[m[1]]) return m[1];
    }
    const tokens = (loc.key || '').split(' ');
    const last = tokens[tokens.length - 1];
    if (tokens.length >= 3 && CODE_TO_STATE_NAME[last]) return last;
    return null;
};

// Months covered by the applied range, oldest first, as 'YYYY-MM'
const monthsBetween = (startDate, endDate) => {
    const months = [];
    const d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    while (d <= end) {
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        d.setMonth(d.getMonth() + 1);
    }
    return months;
};

const formatMonthLabel = (ym) => {
    const [y, m] = ym.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const formatCount = (n) => (n == null ? '—' : n.toLocaleString());

const formatDimensionName = (type) =>
    type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// ---------------------------------------------------------------------------
// Review-theme keyword scan — client-side twin of the slide deck's
// location_extremes tally: % of a location's positive (5★ / NPS 9-10) or
// negative (≤3★ / NPS 0-6) reviews mentioning each theme.
// ---------------------------------------------------------------------------
const REVIEW_THEMES = {
    food: [/\bfood\b/, /\btast/, /\bflavor/, /\bdelicious/, /\bbland\b/, /\bundercooked/,
           /\bovercooked/, /\bsoggy/, /\bstale\b/, /\bportion/, /\bmenu\b/, /\bdish\b/],
    service: [/\bservice\b/, /\bstaff\b/, /\bserver/, /\bwaiter/, /\bwaitress/, /\bbartender/,
              /\bmanager/, /\bemployee/, /front desk/, /\brude\b/, /\bfriendly/, /\battentive/, /\btrainer/],
    speed: [/\bwait(ed|ing)?\b/, /\bslow\b/, /\bfast\b/, /\bquick/, /\bforever\b/,
            /took (over|an hour|too long)/],
    clean: [/\bclean/, /\bdirty/, /\bfilth/, /\bsmell/, /\bnasty/, /\bgross\b/, /\bmold/,
            /sanitiz/, /\bbathroom/, /\brestroom/],
    price: [/\bprice/, /\bexpensive/, /\boverpriced/, /\bvalue\b/, /\bcheap/, /\bcharg/,
            /\bbill/, /\brefund/, /\bfees?\b/, /\bmembership/, /\bworth\b/],
    accuracy: [/wrong order/, /order was wrong/, /\bmissing\b/, /\bforgot/, /\bincorrect/,
               /\bmessed up/, /\bmistake/],
    atmosphere: [/\batmosphere/, /\bambian/, /\bdecor\b/, /\bnoisy/, /\bloud\b/, /\bvibe\b/,
                 /\bmusic\b/, /\bcozy/],
    crowding: [/\bcrowd/, /\bpacked\b/, /\bbusy\b/, /too many people/],
    facilities: [/\bequipment/, /\bmachine/, /\bbroken/, /out of order/, /\blocker/,
                 /\bshower/, /\bsauna\b/, /\bparking/, /\btreadmill/, /\bweights\b/],
};
const THEME_MIN_TEXTS = 3;   // a 100% off one review misleads; show nothing
const THEME_FLOOR_PCT = 15;  // themes below this share of reviews stay hidden

const themesFromPosts = (rawPosts) => {
    const pos = [], neg = [];
    for (const p of rawPosts) {
        const text = (p.text || '').toLowerCase();
        if (!text) continue;
        const isPos = p.rating != null ? p.rating === 5 : p.nps_rating >= 9;
        const isNeg = p.rating != null ? p.rating <= 3 : p.nps_rating <= 6;
        if (isPos) pos.push(text);
        else if (isNeg) neg.push(text);
    }
    const tally = (texts) => {
        if (texts.length < THEME_MIN_TEXTS) return [];
        return Object.entries(REVIEW_THEMES)
            .map(([theme, patterns]) => ({
                theme,
                pct: Math.round((100 * texts.filter(t => patterns.some(rx => rx.test(t))).length) / texts.length),
            }))
            .filter(t => t.pct >= THEME_FLOOR_PCT)
            .sort((a, b) => b.pct - a.pct)
            .slice(0, 3);
    };
    return { pos: tally(pos), neg: tally(neg) };
};

// Star-rating / NPS tag colors for the review audit view, red → green.
// Same pale-chip family as MotivationPost's confidence badge.
const STAR_TAG_COLORS = {
    1: { bg: '#FEE2E2', text: '#991B1B' },
    2: { bg: '#FFEDD5', text: '#9A3412' },
    3: { bg: '#FEF9C3', text: '#854D0E' },
    4: { bg: '#ECFCCB', text: '#4D7C0F' },
    5: { bg: '#DCFCE7', text: '#166534' },
};

const starValueColors = (value) => {
    const str = String(value || '').trim();
    const star = /^([1-5])\/5 stars?$/i.exec(str);
    if (star) return STAR_TAG_COLORS[Number(star[1])];
    // LLM NPS 0-10: promoter ≥8 green, passive 7 amber, detractor ≤6 red
    const nps = /^nps ([0-9]|10)\/10$/i.exec(str);
    if (nps) {
        const score = Number(nps[1]);
        if (score >= 8) return STAR_TAG_COLORS[5];
        if (score === 7) return STAR_TAG_COLORS[3];
        return STAR_TAG_COLORS[1];
    }
    return null;
};

const StatTile = ({ label, value, detail }) => (
    <div style={{
        flex: '1 1 140px', minWidth: 140, padding: '14px 16px',
        background: SURFACE, border: `1px solid ${HAIRLINE}`, borderRadius: 10,
    }}>
        <div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 600, color: INK, lineHeight: 1.1 }}>{value}</div>
        {detail && <div style={{ fontSize: 12, color: INK_SECONDARY, marginTop: 4 }}>{detail}</div>}
    </div>
);

const DivergingLegend = ({ metricInfo }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: INK_SECONDARY }}>
            {metricInfo.fmt(metricInfo.legendLow)} or below
        </span>
        <div style={{ display: 'flex', gap: 2 }}>
            {DIVERGING.map((c, i) => (
                <div key={c} style={{
                    width: 26, height: 12, background: c, borderRadius: 2,
                    border: i === 3 ? `1px solid ${HAIRLINE}` : 'none',
                }} />
            ))}
        </div>
        <span style={{ fontSize: 12, color: INK_SECONDARY }}>
            {metricInfo.fmt(metricInfo.legendHigh)} or above
        </span>
        <span style={{ fontSize: 12, color: INK_MUTED }}>
            · midpoint = {metricInfo.midpointLabel} {metricInfo.fmt(metricInfo.colorMid)}
        </span>
    </div>
);

// Trend view legend: same diverging ramp, but centered at Δ = 0 (no change)
const TrendLegend = ({ metricInfo, windowLabel }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: INK_SECONDARY }}>
            {metricInfo.fmtDelta(-metricInfo.halfRange)} or worse
        </span>
        <div style={{ display: 'flex', gap: 2 }}>
            {DIVERGING.map((c, i) => (
                <div key={c} style={{
                    width: 26, height: 12, background: c, borderRadius: 2,
                    border: i === 3 ? `1px solid ${HAIRLINE}` : 'none',
                }} />
            ))}
        </div>
        <span style={{ fontSize: 12, color: INK_SECONDARY }}>
            {metricInfo.fmtDelta(metricInfo.halfRange)} or better
        </span>
        <span style={{ fontSize: 12, color: INK_MUTED }}>
            · {windowLabel} · gray = under {MOMENTUM_MIN_N} {metricInfo.nLabel} in a window
        </span>
    </div>
);

// ---------------------------------------------------------------------------
// US map: location bubbles or state choropleth, plus optional competitor pins
// ---------------------------------------------------------------------------
const USMap = ({ locations, metricInfo, selectedKey, onSelect, mapMode, stateStats, stateFilter, onToggleState, overlay, trend }) => {
    const containerRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);
    const [zoom, setZoom] = useState({ coordinates: [-96.5, 38.5], zoom: 1 });

    // Same construction react-simple-maps uses internally for this width/height,
    // so "projects to null" here means "would crash the Marker" there.
    const filterProjection = useMemo(
        () => geoAlbersUsa().translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]),
        []
    );

    const { mappable, unmappedCount, maxCount } = useMemo(() => {
        const mappable = [];
        let unmapped = 0;
        let max = 1;
        for (const loc of locations) {
            if (loc.lat != null && loc.lng != null && filterProjection([loc.lng, loc.lat])) {
                mappable.push(loc);
                if (volumeOf(loc) > max) max = volumeOf(loc);
            } else {
                unmapped += 1;
            }
        }
        return { mappable, unmappedCount: unmapped, maxCount: max };
    }, [locations, filterProjection]);

    const overlayPins = useMemo(() => {
        if (!overlay?.data?.locations) return [];
        return overlay.data.locations.filter(
            loc => loc.lat != null && loc.lng != null && filterProjection([loc.lng, loc.lat])
        );
    }, [overlay, filterProjection]);

    const radiusFor = useCallback(
        (count) => 3.5 + 12.5 * Math.sqrt(count / maxCount),
        [maxCount]
    );

    const showTooltip = useCallback((evt, content) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setTooltip({ x: evt.clientX - rect.left, y: evt.clientY - rect.top, ...content });
    }, []);

    if (mapMode === 'locations' && mappable.length === 0) {
        return (
            <div style={{ padding: 24, color: INK_SECONDARY, fontSize: 14 }}>
                No locations with map coordinates in this data set
                {unmappedCount > 0 ? ` (${unmappedCount} locations appear in the rankings below)` : ''}.
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <ComposableMap projection="geoAlbersUsa" width={MAP_WIDTH} height={MAP_HEIGHT} style={{ width: '100%', height: 'auto' }}>
                <ZoomableGroup
                    center={zoom.coordinates}
                    zoom={zoom.zoom}
                    minZoom={1}
                    maxZoom={8}
                    onMoveEnd={(pos) => setZoom(pos)}
                >
                    <Geographies geography={statesTopo}>
                        {({ geographies }) => geographies.map((geo) => {
                            const code = STATE_NAME_TO_CODE[geo.properties.name];
                            const stat = mapMode === 'states' && code ? stateStats[code] : null;
                            const fill = stat
                                ? (trend
                                    ? (stat.delta != null ? divergingColor(stat.delta, 0, metricInfo.halfRange) : '#eceae4')
                                    : divergingColor(stat.value, metricInfo.colorMid, metricInfo.colorHalf))
                                : '#eceae4';
                            const isFiltered = stateFilter && code === stateFilter;
                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    fill={fill}
                                    stroke={isFiltered ? INK : '#ffffff'}
                                    strokeWidth={isFiltered ? 1.5 : 0.75}
                                    onMouseMove={mapMode === 'states' && stat ? (evt) => showTooltip(evt, {
                                        title: trend
                                            ? (stat.delta != null ? metricInfo.fmtDelta(stat.delta) : 'No trend data')
                                            : metricInfo.fmt(stat.value),
                                        line1: geo.properties.name,
                                        line2: `${formatCount(stat.locations)} locations · ${formatCount(stat.n)} ${metricInfo.nLabel}`,
                                        line3: trend && stat.delta == null
                                            ? `needs ≥${MOMENTUM_MIN_N} ${metricInfo.nLabel} in each 3-month window`
                                            : 'click to filter the charts below',
                                    }) : undefined}
                                    onMouseLeave={() => setTooltip(null)}
                                    onClick={mapMode === 'states' && code ? () => onToggleState(code) : undefined}
                                    style={{
                                        default: { outline: 'none', cursor: mapMode === 'states' && stat ? 'pointer' : 'default' },
                                        hover: { outline: 'none', fill: mapMode === 'states' && stat ? fill : '#e4e2db', opacity: mapMode === 'states' && stat ? 0.85 : 1 },
                                        pressed: { outline: 'none' },
                                    }}
                                />
                            );
                        })}
                    </Geographies>
                    {/* Competitor pins render beneath the primary bubbles */}
                    {mapMode === 'locations' && overlayPins.map((loc) => (
                        <Marker key={`ov-${loc.key}`} coordinates={[loc.lng, loc.lat]}>
                            <circle
                                r={2.8}
                                fill={COMPETITOR_COLOR}
                                fillOpacity={0.9}
                                stroke="#ffffff"
                                strokeWidth={0.9}
                                style={{ pointerEvents: 'none' }}
                            />
                            <circle
                                r={8}
                                fill="transparent"
                                onMouseMove={(evt) => showTooltip(evt, {
                                    title: overlay.name,
                                    line1: cityStateFromAddress(loc.address) || nameFromStoreCode(loc.key) || loc.name,
                                    line2: loc.address,
                                    line3: `${formatCount(loc.post_count)} reviews`,
                                })}
                                onMouseLeave={() => setTooltip(null)}
                            />
                        </Marker>
                    ))}
                    {mapMode === 'locations' && mappable.map((loc) => {
                        const r = radiusFor(volumeOf(loc));
                        const isSelected = selectedKey === loc.key;
                        const { value, n } = metricInfo.locValue(loc);
                        const t = trend ? trend.byKey[loc.key] : undefined;
                        const fill = trend
                            ? (t ? divergingColor(t.delta, 0, metricInfo.halfRange) : NO_DATA_BG)
                            : divergingColor(value, metricInfo.colorMid, metricInfo.colorHalf);
                        // Near-average (neutral) and no-data bubbles need a gray
                        // ring to stay visible against the map plane.
                        const isPale = fill === DIVERGING[3] || fill === NO_DATA_BG;
                        return (
                            <Marker key={loc.key} coordinates={[loc.lng, loc.lat]}>
                                <circle
                                    r={r}
                                    fill={fill}
                                    fillOpacity={0.85}
                                    stroke={isSelected ? INK : (isPale ? '#b5b3ac' : '#ffffff')}
                                    strokeWidth={isSelected ? 2 : 1.25}
                                    style={{ pointerEvents: 'none' }}
                                />
                                {/* Transparent hit target, larger than the mark */}
                                <circle
                                    r={Math.max(11, r + 5)}
                                    fill="transparent"
                                    style={{ cursor: 'pointer' }}
                                    onMouseMove={(evt) => showTooltip(evt, {
                                        title: trend
                                            ? (t ? metricInfo.fmtDelta(t.delta) : 'No trend data')
                                            : (value != null ? metricInfo.fmt(value) : `No ${metricInfo.shortLabel} data`),
                                        line1: displayName(loc),
                                        line2: loc.address,
                                        line3: trend
                                            ? (t
                                                ? `${metricInfo.fmtShort(t.prev)} → ${metricInfo.fmtShort(t.last)} · ${formatCount(t.lastN)} recent ${metricInfo.nLabel} · click for details`
                                                : `needs ≥${MOMENTUM_MIN_N} ${metricInfo.nLabel} in each 3-month window · click for details`)
                                            : `${formatCount(n)} ${metricInfo.nLabel} · click for details`,
                                    })}
                                    onMouseLeave={() => setTooltip(null)}
                                    onClick={() => onSelect(loc)}
                                />
                            </Marker>
                        );
                    })}
                </ZoomableGroup>
            </ComposableMap>
            {tooltip && (
                <div style={{
                    position: 'absolute',
                    left: Math.min(tooltip.x + 14, (containerRef.current?.clientWidth || 400) - 230),
                    top: tooltip.y + 14,
                    width: 216,
                    background: '#ffffff',
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 8,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                    padding: '10px 12px',
                    pointerEvents: 'none',
                    zIndex: 5,
                }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: INK }}>{tooltip.title}</div>
                    {tooltip.line1 && <div style={{ fontSize: 13, color: INK, marginTop: 2 }}>{tooltip.line1}</div>}
                    {tooltip.line2 && <div style={{ fontSize: 11, color: INK_MUTED, marginTop: 2 }}>{tooltip.line2}</div>}
                    {tooltip.line3 && <div style={{ fontSize: 12, color: INK_SECONDARY, marginTop: 4 }}>{tooltip.line3}</div>}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 12, color: INK_MUTED }}>
                    {mapMode === 'locations'
                        ? `Bubble size = review volume · color = ${trend ? '3-month trend' : 'the selected metric'} · scroll to zoom, drag to pan`
                        : `States colored by ${trend ? '3-month trend' : 'the selected metric'} · click a state to filter the charts below`}
                </span>
                {mapMode === 'locations' && unmappedCount > 0 && (
                    <span style={{ fontSize: 12, color: INK_MUTED }}>
                        {unmappedCount} locations without map coordinates are included in the rankings below
                    </span>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Top & bottom locations heat chart (rows = locations, columns = months)
// ---------------------------------------------------------------------------
const MIN_RATED_PER_CELL = 3;

const LocationHeatChart = ({ topRows, bottomRows, hiddenBetween, months, metricInfo, selectedKey, onSelectRow, onSelectCell, themesByKey }) => {
    const labelWidth = 190;
    const monthLabelStep = Math.max(1, Math.ceil(months.length / 12));

    const renderRow = (loc, rank) => {
        const monthly = {};
        for (const m of loc.monthly || []) monthly[m.month] = m;
        const isSelected = selectedKey === loc.key;
        const { value: rowValue } = metricInfo.locValue(loc);
        // Above-average rows explain themselves with what positive reviews
        // praise; below-average rows with what critical reviews cite.
        const isAbove = rowValue != null && metricInfo.midpoint != null && rowValue >= metricInfo.midpoint;
        const themeInfo = themesByKey ? themesByKey[loc.key] : undefined;
        const themeList = themeInfo ? (isAbove ? themeInfo.pos : themeInfo.neg) : null;
        const themeLine = themeList && themeList.length > 0
            ? themeList.map(t => `${t.theme} ${t.pct}%`).join(' · ')
            : null;
        return (
            <div key={loc.key} style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                <div
                    onClick={() => onSelectRow(loc)}
                    title={`${loc.address || loc.key}${themeLine
                        ? `\n${isAbove ? 'In positive reviews' : 'In critical reviews'}: ${themeLine}`
                        : ''}`}
                    style={{
                        width: labelWidth, minWidth: labelWidth, display: 'flex', alignItems: 'center', gap: 6,
                        cursor: 'pointer', padding: '2px 4px', borderRadius: 4,
                        background: isSelected ? '#f0f4fa' : 'transparent',
                    }}
                >
                    <span style={{ fontSize: 11, color: INK_MUTED, width: 22, textAlign: 'right' }}>{rank}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                            display: 'block', fontSize: 12.5, color: INK, whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: isSelected ? 600 : 400,
                        }}>
                            {displayName(loc)}
                        </span>
                        {themeLine && (
                            <span style={{
                                display: 'block', fontSize: 10.5, lineHeight: '13px',
                                color: isAbove ? '#1c5cab' : '#c73230',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {themeLine}
                            </span>
                        )}
                    </span>
                </div>
                <div style={{ display: 'flex', flex: 1, gap: 2 }}>
                    {months.map((m) => {
                        const cell = monthly[m];
                        const cv = cell ? metricInfo.cellValue(cell) : null;
                        const hasSignal = cv && cv.n >= MIN_RATED_PER_CELL && cv.value != null;
                        const bg = hasSignal ? divergingColor(cv.value, metricInfo.colorMid, metricInfo.colorHalf) : NO_DATA_BG;
                        const label = cell
                            ? `${formatMonthLabel(m)} — ${cv.value != null ? metricInfo.fmt(cv.value) : `no ${metricInfo.shortLabel} data`} (${cv.n} ${metricInfo.nLabel})${!hasSignal ? ' — too few to color' : ''}`
                            : `${formatMonthLabel(m)} — no reviews`;
                        return (
                            <MuiTooltip key={m} title={`${displayName(loc)} · ${label}`} enterDelay={100} arrow>
                                <div
                                    onClick={() => cell && onSelectCell(loc, m)}
                                    style={{
                                        flex: 1, minWidth: 12, height: 24, borderRadius: 3,
                                        background: bg,
                                        border: hasSignal ? 'none' : `1px dashed ${HAIRLINE}`,
                                        boxSizing: 'border-box',
                                        cursor: cell ? 'pointer' : 'default',
                                    }}
                                />
                            </MuiTooltip>
                        );
                    })}
                </div>
                <div style={{
                    width: 58, minWidth: 58, textAlign: 'center', fontSize: 12, fontWeight: 600,
                    color: INK, background: divergingColor(rowValue, metricInfo.colorMid, metricInfo.colorHalf),
                    borderRadius: 4, padding: '3px 0', marginLeft: 4,
                    // Light arm ends are low-contrast fills; dark ink stays readable on all bins
                }}>
                    {rowValue != null ? metricInfo.fmtShort(rowValue) : '—'}
                </div>
                <div style={{ width: 60, minWidth: 60, textAlign: 'right', fontSize: 11.5, color: INK_MUTED }}>
                    {formatCount(metricInfo.locValue(loc).n)}
                </div>
            </div>
        );
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: Math.max(640, labelWidth + months.length * 14 + 130) }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 6, alignItems: 'baseline' }}>
                    <div style={{ width: labelWidth, minWidth: labelWidth, fontSize: 11, color: INK_MUTED }}>
                        Location
                    </div>
                    <div style={{ flex: 1 }} />
                    <div style={{ width: 58, minWidth: 58, textAlign: 'center', fontSize: 11, color: INK_MUTED }}>{metricInfo.columnLabel}</div>
                    <div style={{ width: 60, minWidth: 60, textAlign: 'right', fontSize: 11, color: INK_MUTED }}>{metricInfo.nColumnLabel}</div>
                </div>
                {topRows.map((loc, i) => renderRow(loc, i + 1))}
                {bottomRows.length > 0 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0',
                        color: INK_MUTED, fontSize: 11.5,
                    }}>
                        <div style={{ flex: 1, borderTop: `1px dashed ${HAIRLINE}` }} />
                        {hiddenBetween > 0 ? `${formatCount(hiddenBetween)} locations between` : 'bottom of ranking'}
                        <div style={{ flex: 1, borderTop: `1px dashed ${HAIRLINE}` }} />
                    </div>
                )}
                {bottomRows.map((loc, i) => renderRow(loc, topRows.length + hiddenBetween + i + 1))}
                {/* Month axis */}
                <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                    <div style={{ width: labelWidth, minWidth: labelWidth }} />
                    <div style={{ display: 'flex', flex: 1, gap: 2 }}>
                        {months.map((m, i) => (
                            <div key={m} style={{ flex: 1, minWidth: 12, fontSize: 10, color: INK_MUTED, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'visible' }}>
                                {i % monthLabelStep === 0 ? formatMonthLabel(m) : ''}
                            </div>
                        ))}
                    </div>
                    <div style={{ width: 122, minWidth: 122 }} />
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Momentum: biggest movers, last 3 months vs the 3 before
// ---------------------------------------------------------------------------
const MomentumList = ({ improving, declining, metricInfo, onSelectRow, selectedKey }) => {
    const renderRow = (row) => {
        const isSelected = selectedKey === row.loc.key;
        return (
            <div
                key={row.loc.key}
                onClick={() => onSelectRow(row.loc)}
                title={row.loc.address || row.loc.key}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                    borderRadius: 6, cursor: 'pointer',
                    background: isSelected ? '#f0f4fa' : 'transparent',
                }}
            >
                <span style={{
                    fontSize: 12, fontWeight: 700, width: 56, textAlign: 'center',
                    color: INK, background: divergingColor(row.delta, 0, metricInfo.halfRange),
                    borderRadius: 4, padding: '3px 0',
                }}>
                    {metricInfo.fmtDelta(row.delta)}
                </span>
                <span style={{
                    fontSize: 12.5, color: INK, flex: 1, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: isSelected ? 600 : 400,
                }}>
                    {displayName(row.loc)}
                </span>
                <span style={{ fontSize: 11.5, color: INK_SECONDARY, whiteSpace: 'nowrap' }}>
                    {metricInfo.fmtShort(row.prev)} → {metricInfo.fmtShort(row.last)}
                </span>
                <span style={{ fontSize: 11, color: INK_MUTED, width: 52, textAlign: 'right' }}>
                    n={formatCount(row.lastN)}
                </span>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 320px', minWidth: 300 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1c5cab', marginBottom: 4 }}>Improving</div>
                {improving.length > 0
                    ? improving.map(renderRow)
                    : <div style={{ fontSize: 12.5, color: INK_MUTED, padding: 6 }}>No locations improving with enough data.</div>}
            </div>
            <div style={{ flex: '1 1 320px', minWidth: 300 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#c73230', marginBottom: 4 }}>Declining</div>
                {declining.length > 0
                    ? declining.map(renderRow)
                    : <div style={{ fontSize: 12.5, color: INK_MUTED, padding: 6 }}>No locations declining with enough data.</div>}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Regions: ranked states
// ---------------------------------------------------------------------------
const RegionList = ({ regions, metricInfo, stateFilter, onToggleState }) => (
    <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '2px 20px',
    }}>
        {regions.map((r, i) => (
            <div
                key={r.code}
                onClick={() => onToggleState(r.code)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                    borderRadius: 6, cursor: 'pointer',
                    background: stateFilter === r.code ? '#f0f4fa' : 'transparent',
                }}
            >
                <span style={{ fontSize: 11, color: INK_MUTED, width: 20, textAlign: 'right' }}>{i + 1}</span>
                <span style={{
                    fontSize: 12.5, color: INK, flex: 1,
                    fontWeight: stateFilter === r.code ? 600 : 400,
                }}>
                    {CODE_TO_STATE_NAME[r.code] || r.code}
                </span>
                {r.delta != null && (
                    <span style={{ fontSize: 11, color: r.delta >= 0 ? '#1c5cab' : '#c73230', whiteSpace: 'nowrap' }}>
                        {metricInfo.fmtDelta(r.delta)}
                    </span>
                )}
                <span style={{
                    fontSize: 12, fontWeight: 600, width: 52, textAlign: 'center',
                    color: INK, background: divergingColor(r.value, metricInfo.colorMid, metricInfo.colorHalf),
                    borderRadius: 4, padding: '2px 0',
                }}>
                    {metricInfo.fmtShort(r.value)}
                </span>
                <span style={{ fontSize: 11, color: INK_MUTED, width: 68, textAlign: 'right' }}>
                    {r.locations} loc · {formatCount(r.n)}
                </span>
            </div>
        ))}
    </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const LocationInsights = ({ topicMap, companyName, initialRange = 12,
    // Optional shared product selection: adopted when valid for this
    // component's topicMap (a location-data subset of the page's full
    // topic list); selections made here are pushed back up.
    selectedProduct: sharedSelectedProduct, onProductChange }) => {
    // Lazy-initialize from the shared selection (if valid) so local state
    // is never empty on first render — see PricingPlot.js for why an
    // empty initial value lets ProductSelector's own internal auto-select
    // effect win a race against the shared-selection logic below.
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : ''
    );

    // Select a default product if none is selected yet. Prefers the
    // page-wide shared topic selection (if valid here); otherwise falls
    // back to the highest-priority topic with location data. See
    // DimensionNPSPage.js for why this replaces relying on ProductSelector's
    // own internal auto-select.
    useEffect(() => {
        if (selectedProduct) return;
        if (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) {
            setSelectedProduct(sharedSelectedProduct);
            return;
        }
        const sortedTopics = Object.values(topicMap || {}).sort((a, b) => {
            const priorityA = a.priority === undefined ? Infinity : a.priority;
            const priorityB = b.priority === undefined ? Infinity : b.priority;
            return priorityA - priorityB;
        });
        if (sortedTopics[0]) setSelectedProduct(sortedTopics[0].id);
    }, [sharedSelectedProduct, topicMap, selectedProduct]);

    const selectProduct = (productId) => {
        setSelectedProduct(productId);
        if (onProductChange) onProductChange(productId);
    };
    const [selectedRange, setSelectedRange] = useState(initialRange);
    const [metric, setMetric] = useState('stars'); // 'stars' | 'nps' | 'dim:<type>'
    const [applied, setApplied] = useState(null); // { product, range, startDate, endDate }

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const [dimensions, setDimensions] = useState([]);
    const [dimData, setDimData] = useState({});      // `${product}|${type}` -> payload
    const [dimLoading, setDimLoading] = useState(false);

    const [mapMode, setMapMode] = useState('locations'); // 'locations' | 'states'
    const [mapColorBy, setMapColorBy] = useState('current'); // 'current' | 'trend'
    const [colorAnchor, setColorAnchor] = useState('chain'); // 'chain' | 'fixed'
    const [stateFilter, setStateFilter] = useState(null);

    const [overlayCompetitor, setOverlayCompetitor] = useState('');
    const [overlayCache, setOverlayCache] = useState({}); // competitorId -> payload
    const [overlayLoading, setOverlayLoading] = useState(false);

    const [themesByKey, setThemesByKey] = useState({}); // loc.key -> {pos, neg} | null
    const themesFetchedRef = useRef(new Set());

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [posts, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postsError, setPostsError] = useState(null);
    const [postsSubtitle, setPostsSubtitle] = useState('');

    const fetchData = useCallback(async (product, rangeMonths) => {
        if (!product) return;
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - rangeMonths);

        setLoading(true);
        setError(null);
        setSelectedLocation(null);
        setPosts([]);
        setPostsError(null);
        setDimData({});
        setStateFilter(null);
        setOverlayCompetitor('');
        setOverlayCache({});
        setThemesByKey({});
        themesFetchedRef.current = new Set();

        try {
            const [analyticsRes, dimsRes] = await Promise.all([
                apiPost('topic/locations/analytics', {
                    topic_id: product,
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString(),
                }),
                apiPost('topic/locations/dimensions', { topic_id: product }),
            ]);
            if (!analyticsRes.ok) throw new Error(`Failed to fetch location analytics: ${analyticsRes.status}`);
            const json = await analyticsRes.json();
            const payload = json.data || { total_locations: 0, total_posts: 0, locations: [] };
            setData(payload);
            setApplied({ product, range: rangeMonths, startDate, endDate });

            if (dimsRes.ok) {
                const dims = (await dimsRes.json()).data || [];
                setDimensions(dims);
                if (metric.startsWith('dim:') && !dims.some(d => `dim:${d.assessment_type}` === metric)) {
                    setMetric('stars');
                }
            } else {
                setDimensions([]);
            }

            // Survey-only topics have no star ratings — show NPS by default there
            if ((payload.total_rated || 0) === 0 && (payload.total_nps_rated || 0) > 0 && !metric.startsWith('dim:')) {
                setMetric('nps');
            }
        } catch (err) {
            console.error('Error fetching location analytics:', err);
            setError(`Could not load location data: ${err.message}`);
            setData(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metric]);

    // Auto-fetch once the product selector resolves a default
    useEffect(() => {
        if (selectedProduct && !applied && !loading) {
            fetchData(selectedProduct, selectedRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProduct]);

    // Fetch a dimension's analytics when the metric switches to it
    useEffect(() => {
        if (!applied || !metric.startsWith('dim:')) return;
        const type = metric.slice(4);
        const cacheKey = `${applied.product}|${type}`;
        if (dimData[cacheKey]) return;
        let cancelled = false;
        (async () => {
            setDimLoading(true);
            try {
                const response = await apiPost('topic/locations/analytics', {
                    topic_id: applied.product,
                    start_date: applied.startDate.toISOString(),
                    end_date: applied.endDate.toISOString(),
                    dimension: type,
                });
                if (!response.ok) throw new Error(`status ${response.status}`);
                const json = await response.json();
                if (!cancelled) setDimData(prev => ({ ...prev, [cacheKey]: json.data }));
            } catch (err) {
                console.error(`Error fetching dimension ${type}:`, err);
                if (!cancelled) setError(`Could not load ${formatDimensionName(type)} data: ${err.message}`);
            } finally {
                if (!cancelled) setDimLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metric, applied]);

    // Fetch competitor overlay pins when one is selected
    useEffect(() => {
        if (!applied || !overlayCompetitor || overlayCache[overlayCompetitor]) return;
        let cancelled = false;
        (async () => {
            setOverlayLoading(true);
            try {
                const response = await apiPost('topic/locations/competitor-overlay', {
                    topic_id: applied.product,
                    competitor_topic_id: overlayCompetitor,
                });
                if (!response.ok) throw new Error(`status ${response.status}`);
                const json = await response.json();
                if (!cancelled) setOverlayCache(prev => ({ ...prev, [overlayCompetitor]: json.data }));
            } catch (err) {
                console.error('Error fetching competitor overlay:', err);
                if (!cancelled) setOverlayCache(prev => ({ ...prev, [overlayCompetitor]: { locations: [] } }));
            } finally {
                if (!cancelled) setOverlayLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayCompetitor, applied]);

    const isDimension = metric.startsWith('dim:');
    const dimensionType = isDimension ? metric.slice(4) : null;
    const activeData = isDimension
        ? dimData[applied ? `${applied.product}|${dimensionType}` : '']
        : data;

    const allLocations = useMemo(() => activeData?.locations || [], [activeData]);

    // The NPS view uses LLM assessments when the topic has real coverage,
    // otherwise a star-based proxy (5★ promoter, ≤3★ detractor).
    const npsSource = (data?.total_nps_rated || 0) >= MIN_LLM_NPS_COVERAGE ? 'llm' : 'proxy';

    // Everything the charts need to render the selected metric
    const metricInfo = useMemo(() => {
        const locValue = (loc) => {
            if (isDimension) {
                return { value: loc.avg_score != null ? Number(loc.avg_score) : null, n: loc.scored || 0 };
            }
            if (metric === 'stars') {
                return { value: loc.avg_rating != null ? Number(loc.avg_rating) : null, n: loc.rated_count || 0 };
            }
            if (npsSource === 'llm') {
                return { value: npsOf(loc.nps_promoters, loc.nps_detractors, loc.nps_rated), n: loc.nps_rated || 0 };
            }
            return { value: npsOf(loc.star_promoters, loc.star_detractors, loc.rated_count), n: loc.rated_count || 0 };
        };
        const cellValue = (m) => {
            if (isDimension) {
                return { value: m.avg != null ? Number(m.avg) : null, n: m.scored || 0 };
            }
            if (metric === 'stars') {
                return { value: m.avg != null ? Number(m.avg) : null, n: m.rated || 0 };
            }
            if (npsSource === 'llm') {
                return { value: npsOf(m.nps_promoters, m.nps_detractors, m.nps_rated), n: m.nps_rated || 0 };
            }
            return { value: npsOf(m.star_promoters, m.star_detractors, m.rated), n: m.rated || 0 };
        };
        // Aggregate a set of monthly buckets into one {value, n}
        const aggWindow = (loc, monthSet) => {
            let prom = 0, detr = 0, rated = 0, weighted = 0;
            for (const m of loc.monthly || []) {
                if (!monthSet.has(m.month)) continue;
                if (isDimension) {
                    if (m.avg != null) { weighted += Number(m.avg) * m.scored; rated += m.scored; }
                } else if (metric === 'stars') {
                    if (m.avg != null) { weighted += Number(m.avg) * m.rated; rated += m.rated; }
                } else if (npsSource === 'llm') {
                    prom += m.nps_promoters || 0; detr += m.nps_detractors || 0; rated += m.nps_rated || 0;
                } else {
                    prom += m.star_promoters || 0; detr += m.star_detractors || 0; rated += m.rated || 0;
                }
            }
            if (metric === 'nps') return { value: npsOf(prom, detr, rated), n: rated };
            return { value: rated > 0 ? weighted / rated : null, n: rated };
        };

        // Chain-wide weighted midpoint
        let promoters = 0, detractors = 0, rated = 0, weighted = 0;
        for (const loc of allLocations) {
            const { value, n } = locValue(loc);
            if (value == null || n <= 0) continue;
            if (metric === 'nps') {
                if (npsSource === 'llm') { promoters += loc.nps_promoters || 0; detractors += loc.nps_detractors || 0; rated += loc.nps_rated || 0; }
                else { promoters += loc.star_promoters || 0; detractors += loc.star_detractors || 0; rated += loc.rated_count || 0; }
            } else {
                weighted += value * n;
                rated += n;
            }
        }
        const midpoint = metric === 'nps'
            ? npsOf(promoters, detractors, rated)
            : (rated > 0 ? weighted / rated : null);

        if (metric === 'stars') {
            // Fixed anchor: scale midpoint (3★ on the 1-5 scale), wide enough to
            // span the whole scale so color reads as absolute quality.
            const colorMid = colorAnchor === 'fixed' ? 3 : midpoint;
            const colorHalf = colorAnchor === 'fixed' ? 2 : 0.75;
            return {
                key: 'stars', locValue, cellValue, aggWindow, midpoint,
                halfRange: 0.75,
                colorMid, colorHalf,
                legendLow: colorMid != null ? Math.max(1, colorMid - colorHalf) : 1,
                legendHigh: colorMid != null ? Math.min(5, colorMid + colorHalf) : 5,
                fmt: (v) => `${v.toFixed(2)}★`,
                fmtShort: (v) => v.toFixed(2),
                fmtDelta: (v) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}`,
                shortLabel: 'rating', nLabel: 'reviews',
                columnLabel: 'Avg ★', nColumnLabel: 'Reviews',
                midpointLabel: colorAnchor === 'fixed' ? 'scale midpoint' : 'chain average',
                kpiLabel: 'Chain average', kpiDetail: 'weighted by review count',
                sourceNote: null,
            };
        }
        if (metric === 'nps') {
            const fmtNps = (v) => `${v < 0 ? '−' : '+'}${Math.abs(Math.round(v))}`;
            // Fixed anchor: NPS 0 (more promoters than detractors), ±50 covers
            // the conventional bad ↔ excellent range.
            const colorMid = colorAnchor === 'fixed' ? 0 : midpoint;
            const colorHalf = colorAnchor === 'fixed' ? 50 : 40;
            return {
                key: 'nps', locValue, cellValue, aggWindow, midpoint,
                halfRange: 40,
                colorMid, colorHalf,
                legendLow: colorMid != null ? Math.max(-100, colorMid - colorHalf) : -40,
                legendHigh: colorMid != null ? Math.min(100, colorMid + colorHalf) : 40,
                fmt: fmtNps, fmtShort: fmtNps, fmtDelta: fmtNps,
                shortLabel: 'NPS',
                nLabel: npsSource === 'llm' ? 'NPS-scored posts' : 'reviews',
                columnLabel: 'NPS', nColumnLabel: npsSource === 'llm' ? 'Scored' : 'Reviews',
                midpointLabel: colorAnchor === 'fixed' ? 'zero NPS' : 'chain NPS',
                kpiLabel: 'Chain NPS',
                kpiDetail: npsSource === 'llm'
                    ? 'LLM-assessed · promoters ≥8, detractors ≤6'
                    : 'star-based proxy · 5★ promoters, ≤3★ detractors',
                sourceNote: npsSource === 'llm'
                    ? 'NPS from LLM assessments of each post'
                    : 'No LLM NPS coverage for this product — showing a star-based proxy (5★ = promoter, ≤3★ = detractor)',
            };
        }
        // Dimension metric: 0-10 (some, like pricing, span -10..10). The color
        // half-range adapts to the spread across locations.
        let varSum = 0;
        if (rated > 0) {
            for (const loc of allLocations) {
                const { value, n } = locValue(loc);
                if (value == null || n <= 0) continue;
                varSum += n * Math.pow(value - midpoint, 2);
            }
        }
        const std = rated > 0 ? Math.sqrt(varSum / rated) : 0;
        const halfRange = Math.max(0.6, 1.5 * std);
        const dimName = formatDimensionName(dimensionType);
        // Clamp the legend to observed values (dimension scales vary: most are
        // 0-10, pricing is -10..10) so it never advertises impossible scores
        let obsMin = Infinity, obsMax = -Infinity;
        for (const loc of allLocations) {
            const { value, n } = locValue(loc);
            if (value == null || n <= 0) continue;
            if (value < obsMin) obsMin = value;
            if (value > obsMax) obsMax = value;
        }
        // Fixed anchor: scale midpoint — most dimensions run 0-10 (mid 5), but
        // negative observations mean a -10..10 scale (e.g. pricing, mid 0).
        const fixedMid = obsMin < 0 ? 0 : 5;
        const fixedHalf = obsMin < 0 ? 10 : 5;
        const colorMid = colorAnchor === 'fixed' ? fixedMid : midpoint;
        const colorHalf = colorAnchor === 'fixed' ? fixedHalf : halfRange;
        return {
            key: metric, locValue, cellValue, aggWindow, midpoint,
            halfRange,
            colorMid, colorHalf,
            legendLow: colorMid != null
                ? (colorAnchor === 'fixed' ? colorMid - colorHalf : Math.max(obsMin, colorMid - colorHalf))
                : -1,
            legendHigh: colorMid != null
                ? (colorAnchor === 'fixed' ? colorMid + colorHalf : Math.min(obsMax, colorMid + colorHalf))
                : 1,
            fmt: (v) => v.toFixed(1),
            fmtShort: (v) => v.toFixed(1),
            fmtDelta: (v) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}`,
            shortLabel: dimName, nLabel: 'scored posts',
            columnLabel: 'Score', nColumnLabel: 'Scored',
            midpointLabel: colorAnchor === 'fixed' ? 'scale midpoint' : 'chain average',
            kpiLabel: `${dimName} (chain)`,
            kpiDetail: 'LLM-scored from review text',
            sourceNote: `${dimName}: each post is scored by the LLM assessment pipeline; locations are colored against ${colorAnchor === 'fixed' ? 'the scale midpoint' : 'the chain average'}.`,
        };
    }, [allLocations, metric, npsSource, isDimension, dimensionType, colorAnchor]);

    const months = useMemo(() => {
        if (!applied) return [];
        return monthsBetween(applied.startDate, applied.endDate);
    }, [applied]);

    // Optional state filter for the ranking/momentum charts
    const locations = useMemo(() => {
        if (!stateFilter) return allLocations;
        return allLocations.filter(loc => stateOf(loc) === stateFilter);
    }, [allLocations, stateFilter]);

    // Momentum windows: 3 months ending at the newest month WITH data vs the
    // 3 before (data can trail the calendar by a month or two).
    const momentumWindows = useMemo(() => {
        if (months.length < 6) return null;
        let maxIdx = -1;
        for (const loc of allLocations) {
            for (const m of loc.monthly || []) {
                const idx = months.indexOf(m.month);
                if (idx > maxIdx) maxIdx = idx;
            }
        }
        if (maxIdx < 5) return null;
        const lastMonths = months.slice(maxIdx - 2, maxIdx + 1);
        const prevMonths = months.slice(maxIdx - 5, maxIdx - 2);
        return {
            last: new Set(lastMonths),
            prev: new Set(prevMonths),
            label: `${formatMonthLabel(lastMonths[0])}–${formatMonthLabel(lastMonths[2])} vs ${formatMonthLabel(prevMonths[0])}–${formatMonthLabel(prevMonths[2])}`,
        };
    }, [allLocations, months]);

    // Region (state) rollup — always computed from ALL locations
    const regions = useMemo(() => {
        const byState = {};
        const lastWindow = momentumWindows ? momentumWindows.last : new Set();
        const prevWindow = momentumWindows ? momentumWindows.prev : new Set();
        for (const loc of allLocations) {
            const code = stateOf(loc);
            if (!code) continue;
            const { value, n } = metricInfo.locValue(loc);
            if (value == null || n <= 0) continue;
            if (!byState[code]) byState[code] = { code, weighted: 0, n: 0, locations: 0, lastW: 0, lastN: 0, prevW: 0, prevN: 0 };
            const s = byState[code];
            s.weighted += value * n;
            s.n += n;
            s.locations += 1;
            const lw = metricInfo.aggWindow(loc, lastWindow);
            const pw = metricInfo.aggWindow(loc, prevWindow);
            if (lw.value != null) { s.lastW += lw.value * lw.n; s.lastN += lw.n; }
            if (pw.value != null) { s.prevW += pw.value * pw.n; s.prevN += pw.n; }
        }
        return Object.values(byState)
            .map(s => ({
                code: s.code,
                value: s.weighted / s.n,
                n: s.n,
                locations: s.locations,
                delta: (s.lastN >= MOMENTUM_MIN_N && s.prevN >= MOMENTUM_MIN_N)
                    ? (s.lastW / s.lastN) - (s.prevW / s.prevN)
                    : null,
            }))
            .sort((a, b) => b.value - a.value);
    }, [allLocations, metricInfo, momentumWindows]);

    const stateStats = useMemo(() => {
        const map = {};
        for (const r of regions) map[r.code] = r;
        return map;
    }, [regions]);

    // Per-location momentum for the map's trend view. Built from allLocations
    // (not the state-filtered list) so toggling a state filter doesn't recolor.
    const trendByKey = useMemo(() => {
        if (!momentumWindows) return null;
        const map = {};
        for (const loc of allLocations) {
            const last = metricInfo.aggWindow(loc, momentumWindows.last);
            const prev = metricInfo.aggWindow(loc, momentumWindows.prev);
            map[loc.key] = (last.value != null && prev.value != null
                && last.n >= MOMENTUM_MIN_N && prev.n >= MOMENTUM_MIN_N)
                ? { delta: last.value - prev.value, last: last.value, prev: prev.value, lastN: last.n }
                : null;
        }
        return map;
    }, [allLocations, metricInfo, momentumWindows]);

    const mapTrend = (mapColorBy === 'trend' && trendByKey)
        ? { byKey: trendByKey, windowLabel: momentumWindows.label }
        : null;

    // Momentum: biggest movers among the (possibly state-filtered) locations
    const momentum = useMemo(() => {
        if (!momentumWindows) return null;
        const rows = [];
        for (const loc of locations) {
            const last = metricInfo.aggWindow(loc, momentumWindows.last);
            const prev = metricInfo.aggWindow(loc, momentumWindows.prev);
            if (last.value == null || prev.value == null) continue;
            if (last.n < MOMENTUM_MIN_N || prev.n < MOMENTUM_MIN_N) continue;
            rows.push({ loc, delta: last.value - prev.value, last: last.value, prev: prev.value, lastN: last.n });
        }
        rows.sort((a, b) => b.delta - a.delta);
        return {
            improving: rows.filter(r => r.delta > 0).slice(0, 8),
            declining: rows.filter(r => r.delta < 0).slice(-8).reverse(),
            qualified: rows.length,
            windowLabel: momentumWindows.label,
        };
    }, [locations, metricInfo, momentumWindows]);

    // Top/bottom ranking among locations with enough data points to be fair
    const { topRows, bottomRows, hiddenBetween, minReviews } = useMemo(() => {
        const scored = locations
            .map(loc => ({ loc, m: metricInfo.locValue(loc) }))
            .filter(({ m }) => m.value != null && m.n > 0);
        let threshold = 25;
        let qualified = scored.filter(({ m }) => m.n >= threshold);
        if (qualified.length < 20) {
            threshold = 5;
            qualified = scored.filter(({ m }) => m.n >= threshold);
        }
        if (qualified.length < 4) {
            threshold = 1;
            qualified = scored;
        }
        qualified.sort((a, b) => b.m.value - a.m.value);
        const rows = qualified.map(q => q.loc);
        if (rows.length <= 20) {
            return { topRows: rows, bottomRows: [], hiddenBetween: 0, minReviews: threshold };
        }
        return {
            topRows: rows.slice(0, 10),
            bottomRows: rows.slice(-10),
            hiddenBetween: rows.length - 20,
            minReviews: threshold,
        };
    }, [locations, metricInfo]);

    // Keyword themes for the ranked rows: fetch each row's recent reviews once
    // per applied range (newest ≤200, same endpoint as the click-through) and
    // scan them client-side. Batched so 20 rows don't hit the backend at once.
    useEffect(() => {
        if (!applied) return;
        const wanted = [...topRows, ...bottomRows].filter(loc => !themesFetchedRef.current.has(loc.key));
        if (wanted.length === 0) return;
        for (const loc of wanted) themesFetchedRef.current.add(loc.key);
        const stored = new Set();
        let cancelled = false;
        (async () => {
            const BATCH = 4;
            for (let i = 0; i < wanted.length && !cancelled; i += BATCH) {
                const results = await Promise.all(wanted.slice(i, i + BATCH).map(async (loc) => {
                    try {
                        const response = await apiPost('topic/locations/posts', {
                            topic_id: applied.product,
                            location_key: loc.key,
                            start_date: applied.startDate.toISOString(),
                            end_date: applied.endDate.toISOString(),
                        });
                        if (!response.ok) throw new Error(`status ${response.status}`);
                        const json = await response.json();
                        return { key: loc.key, themes: themesFromPosts(Array.isArray(json.data) ? json.data : []) };
                    } catch (err) {
                        console.error(`Error fetching review themes for ${loc.key}:`, err);
                        return { key: loc.key, themes: null };
                    }
                }));
                if (cancelled) break;
                for (const r of results) stored.add(r.key);
                setThemesByKey(prev => {
                    const next = { ...prev };
                    for (const r of results) next[r.key] = r.themes;
                    return next;
                });
            }
        })();
        return () => {
            cancelled = true;
            // Un-mark rows whose fetch never landed so a later pass retries them
            for (const loc of wanted) {
                if (!stored.has(loc.key)) themesFetchedRef.current.delete(loc.key);
            }
        };
    }, [topRows, bottomRows, applied]);

    const fetchPosts = useCallback(async (loc, startDate, endDate, subtitle) => {
        if (!applied) return;
        setPostsLoading(true);
        setPostsError(null);
        setPosts([]);
        setSelectedLocation(loc.key);
        setPostsSubtitle(subtitle);

        try {
            const response = await apiPost('topic/locations/posts', {
                topic_id: applied.product,
                location_key: loc.key,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
            });
            if (!response.ok) throw new Error(`Failed to fetch posts: ${response.status}`);
            const json = await response.json();
            const rawPosts = json.data ?? [];
            // Normalize for MotivationPost: star rating (or LLM NPS score) as the
            // value tag, confidence hidden (it renders as "X/10"), epoch -> ISO.
            // rating is kept so the sort dropdown still orders by score.
            const normalized = (Array.isArray(rawPosts) ? rawPosts : []).map(p => ({
                ...p,
                value: p.rating != null
                    ? `${p.rating}/5 stars`
                    : (p.nps_rating != null ? `NPS ${p.nps_rating}/10` : displayName(loc)),
                rating: p.rating ?? p.nps_rating ?? undefined,
                confidence: null,
                time: p.time && p.time < 1e12 ? new Date(p.time * 1000).toISOString() : p.time,
            }));
            setPosts(normalized);
            if (normalized.length === 0) setPostsError('No reviews found for this selection.');
        } catch (err) {
            console.error('Error fetching location posts:', err);
            setPostsError(`Could not load reviews: ${err.message}`);
        } finally {
            setPostsLoading(false);
        }
    }, [applied]);

    const handleSelectLocation = useCallback((loc) => {
        if (!applied) return;
        fetchPosts(loc, applied.startDate, applied.endDate, `${displayName(loc)} · last ${applied.range} months`);
    }, [applied, fetchPosts]);

    const handleSelectCell = useCallback((loc, ym) => {
        const [y, m] = ym.split('-').map(Number);
        const start = new Date(y, m - 1, 1);
        const end = new Date(y, m, 0, 23, 59, 59);
        fetchPosts(loc, start, end, `${displayName(loc)} · ${formatMonthLabel(ym)}`);
    }, [fetchPosts]);

    const handleClosePosts = useCallback(() => {
        setPosts([]);
        setPostsError(null);
        setSelectedLocation(null);
    }, []);

    const handleToggleState = useCallback((code) => {
        setStateFilter(prev => (prev === code ? null : code));
    }, []);

    const competitors = useMemo(() => {
        const list = topicMap?.[applied?.product]?.competitors || [];
        return list.filter(c => c && c.id && c.topic);
    }, [topicMap, applied]);

    const overlay = useMemo(() => {
        if (!overlayCompetitor) return null;
        const comp = competitors.find(c => c.id === overlayCompetitor);
        return {
            name: comp?.topic || 'Competitor',
            data: overlayCache[overlayCompetitor] || null,
        };
    }, [overlayCompetitor, overlayCache, competitors]);

    const chartsBusy = loading || (isDimension && dimLoading && !activeData);
    const hasData = !chartsBusy && activeData && allLocations.length > 0;
    const metricLabel = isDimension ? formatDimensionName(dimensionType) : (metric === 'stars' ? 'average rating' : 'NPS');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Controls — the li-* classNames exist for the Report Generator's
                off-screen capture, which hides interactive chrome with
                capture-scoped CSS; they carry no styles of their own. */}
            <div className="li-controls" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <ProductSelector
                    topicMap={topicMap}
                    selectedId={selectedProduct}
                    onSelect={selectProduct}
                    label="Product"
                    sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="location-range-label">Time Range</InputLabel>
                    <Select
                        labelId="location-range-label"
                        value={selectedRange}
                        label="Time Range"
                        onChange={(e) => setSelectedRange(e.target.value)}
                    >
                        {TIME_RANGE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    size="small"
                    disabled={!selectedProduct || loading}
                    onClick={() => fetchData(selectedProduct, selectedRange)}
                    sx={{ textTransform: 'none' }}
                >
                    Apply
                </Button>
                <FormControl size="small" sx={{ minWidth: 190, marginLeft: 'auto' }}>
                    <InputLabel id="location-metric-label">Metric</InputLabel>
                    <Select
                        labelId="location-metric-label"
                        value={metric}
                        label="Metric"
                        onChange={(e) => setMetric(e.target.value)}
                    >
                        <MenuItem value="stars">Star rating</MenuItem>
                        <MenuItem value="nps">NPS</MenuItem>
                        {dimensions.length > 0 && <Divider />}
                        {dimensions.length > 0 && (
                            <ListSubheader sx={{ lineHeight: '28px' }}>Dimensions</ListSubheader>
                        )}
                        {dimensions.map(d => (
                            <MenuItem key={d.assessment_type} value={`dim:${d.assessment_type}`}>
                                {formatDimensionName(d.assessment_type)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {chartsBusy && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 32, justifyContent: 'center' }}>
                    <CircularProgress size={22} />
                    <span style={{ color: INK_SECONDARY, fontSize: 14 }}>Loading location analytics…</span>
                </div>
            )}

            {error && !chartsBusy && (
                <div style={{ padding: 16, color: '#b3261e', fontSize: 14 }}>{error}</div>
            )}

            {!chartsBusy && !error && activeData && allLocations.length === 0 && (
                <div style={{ padding: 24, color: INK_SECONDARY, fontSize: 14 }}>
                    No location data for this selection in the chosen time range.
                </div>
            )}

            {hasData && metricInfo.midpoint != null && (
                <>
                    {/* KPI row */}
                    <div className="li-kpi-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <StatTile
                            label="Locations"
                            value={formatCount(activeData.total_locations)}
                            detail={activeData.total_locations > allLocations.length
                                ? `showing top ${formatCount(allLocations.length)} by volume`
                                : 'with review activity'}
                        />
                        <StatTile
                            label={isDimension ? 'Scored posts' : 'Reviews analyzed'}
                            value={formatCount(isDimension ? activeData.total_scored : activeData.total_posts)}
                            detail={`last ${applied.range} months`}
                        />
                        <StatTile
                            label={metricInfo.kpiLabel}
                            value={metricInfo.fmt(metricInfo.midpoint)}
                            detail={metricInfo.kpiDetail}
                        />
                        {topRows.length > 0 && (() => {
                            const best = metricInfo.locValue(topRows[0]).value;
                            const worstLoc = bottomRows.length ? bottomRows[bottomRows.length - 1] : topRows[topRows.length - 1];
                            const worst = metricInfo.locValue(worstLoc).value;
                            return (
                                <StatTile
                                    label="Spread (best → worst)"
                                    value={`${metricInfo.fmtShort(best)} → ${metricInfo.fmtShort(worst)}`}
                                    detail={`among locations with ≥${minReviews} ${metricInfo.nLabel}`}
                                />
                            );
                        })()}
                    </div>

                    {metricInfo.sourceNote && (
                        <div className="li-source-note" style={{ fontSize: 12.5, color: INK_SECONDARY, padding: '8px 12px', background: NO_DATA_BG, border: `1px solid ${HAIRLINE}`, borderRadius: 8 }}>
                            {metricInfo.sourceNote}
                        </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <DivergingLegend metricInfo={metricInfo} />
                        {stateFilter && (
                            <Chip
                                size="small"
                                label={`Filtered: ${CODE_TO_STATE_NAME[stateFilter] || stateFilter}`}
                                onDelete={() => setStateFilter(null)}
                            />
                        )}
                        <MuiTooltip title="Chain average colors each location relative to this company; fixed scale anchors color to the scale midpoint so it reads as absolute quality">
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={colorAnchor}
                                onChange={(e, v) => { if (v) setColorAnchor(v); }}
                                sx={{ marginLeft: 'auto' }}
                            >
                                <ToggleButton value="chain" sx={{ textTransform: 'none', px: 1.5, py: 0.4 }}>Chain average</ToggleButton>
                                <ToggleButton value="fixed" sx={{ textTransform: 'none', px: 1.5, py: 0.4 }}>Fixed scale</ToggleButton>
                            </ToggleButtonGroup>
                        </MuiTooltip>
                    </div>

                    {/* Map */}
                    <div className="li-map-section" style={{ border: `1px solid ${HAIRLINE}`, borderRadius: 12, padding: '12px 12px 8px', background: SURFACE }}>
                        <div className="li-map-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginRight: 'auto' }}>
                                {companyName ? `${companyName} — ` : ''}location performance map
                            </div>
                            {competitors.length > 0 && mapMode === 'locations' && (
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                    <InputLabel id="overlay-competitor-label">Overlay competitor</InputLabel>
                                    <Select
                                        labelId="overlay-competitor-label"
                                        value={overlayCompetitor}
                                        label="Overlay competitor"
                                        onChange={(e) => setOverlayCompetitor(e.target.value)}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {competitors.map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.topic}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {trendByKey && (
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={mapColorBy}
                                    onChange={(e, v) => { if (v) setMapColorBy(v); }}
                                >
                                    <ToggleButton value="current" sx={{ textTransform: 'none', px: 1.5 }}>Current</ToggleButton>
                                    <ToggleButton value="trend" sx={{ textTransform: 'none', px: 1.5 }}>Trend</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={mapMode}
                                onChange={(e, v) => { if (v) setMapMode(v); }}
                            >
                                <ToggleButton value="locations" sx={{ textTransform: 'none', px: 1.5 }}>Locations</ToggleButton>
                                <ToggleButton value="states" sx={{ textTransform: 'none', px: 1.5 }}>States</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                        {mapTrend && (
                            <div style={{ marginBottom: 6 }}>
                                <TrendLegend metricInfo={metricInfo} windowLabel={mapTrend.windowLabel} />
                            </div>
                        )}
                        {overlay && mapMode === 'locations' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: INK_SECONDARY, marginBottom: 4 }}>
                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: COMPETITOR_COLOR, display: 'inline-block' }} />
                                {overlayLoading
                                    ? `Loading ${overlay.name} locations…`
                                    : `${overlay.name}: ${formatCount(overlay.data?.locations?.length || 0)} locations shown for reference (not colored by performance)`}
                            </div>
                        )}
                        <USMap
                            locations={locations}
                            metricInfo={metricInfo}
                            selectedKey={selectedLocation}
                            onSelect={handleSelectLocation}
                            mapMode={mapMode}
                            stateStats={stateStats}
                            stateFilter={stateFilter}
                            onToggleState={handleToggleState}
                            overlay={overlay}
                            trend={mapTrend}
                        />
                    </div>

                    {/* Regions */}
                    {regions.length > 1 && (
                        <div className="li-regions-section" style={{ border: `1px solid ${HAIRLINE}`, borderRadius: 12, padding: 12, background: SURFACE }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                                Regions by {metricLabel}
                            </div>
                            <div style={{ fontSize: 12, color: INK_MUTED, margin: '2px 0 10px' }}>
                                Weighted by volume · Δ = last 3 months vs prior 3 · click a state to filter the charts
                            </div>
                            <RegionList
                                regions={regions}
                                metricInfo={metricInfo}
                                stateFilter={stateFilter}
                                onToggleState={handleToggleState}
                            />
                        </div>
                    )}

                    {/* Momentum */}
                    {momentum && (momentum.improving.length > 0 || momentum.declining.length > 0) && (
                        <div className="li-momentum-section" style={{ border: `1px solid ${HAIRLINE}`, borderRadius: 12, padding: 12, background: SURFACE }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                                Biggest movers — {metricLabel}
                            </div>
                            <div style={{ fontSize: 12, color: INK_MUTED, margin: '2px 0 10px' }}>
                                {momentum.windowLabel} · locations with ≥{MOMENTUM_MIN_N} {metricInfo.nLabel} in each window
                                ({momentum.qualified} qualify{stateFilter ? ` in ${CODE_TO_STATE_NAME[stateFilter]}` : ''}) · click to read the reviews
                            </div>
                            <MomentumList
                                improving={momentum.improving}
                                declining={momentum.declining}
                                metricInfo={metricInfo}
                                onSelectRow={handleSelectLocation}
                                selectedKey={selectedLocation}
                            />
                        </div>
                    )}

                    {/* Heat chart */}
                    <div className="li-heat-section" style={{ border: `1px solid ${HAIRLINE}`, borderRadius: 12, padding: 12, background: SURFACE }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                            Top & bottom locations by {metricLabel}
                        </div>
                        <div style={{ fontSize: 12, color: INK_MUTED, margin: '2px 0 12px' }}>
                            Monthly {metricLabel} vs. {metricInfo.midpointLabel} · locations with ≥{minReviews} {metricInfo.nLabel}
                            {stateFilter ? ` · ${CODE_TO_STATE_NAME[stateFilter]} only` : ''} ·
                            click a row or cell to read the reviews behind it ·
                            keywords under each name = themes in its recent positive (blue) or critical (red) reviews
                        </div>
                        <LocationHeatChart
                            topRows={topRows}
                            bottomRows={bottomRows}
                            hiddenBetween={hiddenBetween}
                            months={months}
                            metricInfo={metricInfo}
                            selectedKey={selectedLocation}
                            onSelectRow={handleSelectLocation}
                            onSelectCell={handleSelectCell}
                            themesByKey={themesByKey}
                        />
                    </div>
                </>
            )}

            {postsLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12 }}>
                    <CircularProgress size={18} />
                    <span style={{ color: INK_SECONDARY, fontSize: 13 }}>Loading reviews…</span>
                </div>
            )}
            {postsError && !postsLoading && (
                <div style={{ padding: 12, color: INK_SECONDARY, fontSize: 13 }}>{postsError}</div>
            )}
            {posts.length > 0 && (
                <MotivationPostsDisplay
                    posts={posts}
                    onClose={handleClosePosts}
                    motivationType="Rating"
                    subtitle={postsSubtitle}
                    autoScroll={true}
                    id="location-posts"
                    valueColors={starValueColors}
                />
            )}
        </div>
    );
};

export default LocationInsights;
// Store-naming helpers reused by the Report Generator's location slides so
// deck tables show the same store labels as this tab.
export { displayName as locationDisplayName, stateOf as locationStateOf };
